import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, GripVertical, Clock, Utensils, MapPin, Bed, Bus, Loader2, Save } from "lucide-react";

const EMPTY_ACTIVITY = { time: "", name: "", description: "", type: "sightseeing" };
const ACTIVITY_TYPES = ["sightseeing", "adventure", "food", "shopping", "transport", "free_time", "check_in", "check_out"];

const EMPTY_DAY = {
  day: 1, title: "", description: "",
  activities: [{ ...EMPTY_ACTIVITY }],
  accommodation: { name: "", type: "hotel", notes: "" },
  meals: [],
  transport: "",
};

export default function AdminItineraryBuilder() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [days, setDays] = useState([{ ...EMPTY_DAY }]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/products/${productId}`),
      api.get(`/products/${productId}/itinerary`).catch(() => ({ data: { itinerary: [] } })),
    ]).then(([pRes, iRes]) => {
      setProduct(pRes.data);
      const existing = iRes.data.itinerary;
      if (existing?.length > 0) {
        setDays(existing.map((d, i) => ({
          ...EMPTY_DAY,
          ...d,
          day: d.day || i + 1,
          activities: d.activities?.length > 0 ? d.activities : [{ ...EMPTY_ACTIVITY }],
          accommodation: d.accommodation || { name: "", type: "hotel", notes: "" },
          meals: d.meals || [],
        })));
      }
    }).catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [productId]);

  const updateDay = (idx, field, value) => {
    setDays((prev) => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  const addDay = () => {
    setDays((prev) => [...prev, { ...EMPTY_DAY, day: prev.length + 1, activities: [{ ...EMPTY_ACTIVITY }] }]);
  };

  const removeDay = (idx) => {
    setDays((prev) => prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 })));
  };

  const updateActivity = (dayIdx, actIdx, field, value) => {
    setDays((prev) => prev.map((d, di) => {
      if (di !== dayIdx) return d;
      return { ...d, activities: d.activities.map((a, ai) => ai === actIdx ? { ...a, [field]: value } : a) };
    }));
  };

  const addActivity = (dayIdx) => {
    setDays((prev) => prev.map((d, i) => i === dayIdx ? { ...d, activities: [...d.activities, { ...EMPTY_ACTIVITY }] } : d));
  };

  const removeActivity = (dayIdx, actIdx) => {
    setDays((prev) => prev.map((d, i) => i === dayIdx ? { ...d, activities: d.activities.filter((_, ai) => ai !== actIdx) } : d));
  };

  const toggleMeal = (dayIdx, meal) => {
    setDays((prev) => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      const meals = d.meals.includes(meal) ? d.meals.filter((m) => m !== meal) : [...d.meals, meal];
      return { ...d, meals };
    }));
  };

  const saveItinerary = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${productId}/itinerary`, {
        itinerary: days,
        total_days: days.length,
        total_nights: Math.max(0, days.length - 1),
      });
      toast.success("Itinerary saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-5xl mx-auto p-8"><div className="h-40 bg-muted animate-pulse rounded-xl" /></div>;

  const typeIcon = { sightseeing: MapPin, adventure: MapPin, food: Utensils, shopping: MapPin, transport: Bus, free_time: Clock, check_in: Bed, check_out: Bed };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="itinerary-builder-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/products")} data-testid="back-to-products">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl truncate">Itinerary Builder</h1>
          {product && <p className="text-sm text-muted-foreground truncate">{product.title}</p>}
        </div>
        <Button onClick={saveItinerary} disabled={saving} data-testid="save-itinerary-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Itinerary
        </Button>
      </div>

      <div className="space-y-6">
        {days.map((day, di) => (
          <Card key={di} className="p-5 relative" data-testid={`itinerary-day-${di}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal/10 text-teal flex items-center justify-center font-bold text-sm">
                  D{day.day}
                </div>
                <Input
                  value={day.title}
                  onChange={(e) => updateDay(di, "title", e.target.value)}
                  placeholder={`Day ${day.day} — e.g., Arrival & Old City`}
                  className="text-lg font-semibold border-none shadow-none px-0 h-auto focus-visible:ring-0"
                  data-testid={`day-${di}-title`}
                />
              </div>
              {days.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => removeDay(di)} className="text-destructive shrink-0" data-testid={`remove-day-${di}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            <Textarea
              value={day.description}
              onChange={(e) => updateDay(di, "description", e.target.value)}
              placeholder="Day overview (optional)"
              rows={2}
              className="mb-4"
            />

            {/* Activities */}
            <div className="space-y-3 mb-4">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Activities</Label>
              {day.activities.map((act, ai) => {
                const Icon = typeIcon[act.type] || MapPin;
                return (
                  <div key={ai} className="flex items-start gap-2 bg-muted/30 rounded-lg p-3" data-testid={`activity-${di}-${ai}`}>
                    <div className="shrink-0 mt-1">
                      <Icon className="w-4 h-4 text-teal" />
                    </div>
                    <div className="flex-1 grid grid-cols-[80px_1fr_120px] gap-2">
                      <Input
                        value={act.time}
                        onChange={(e) => updateActivity(di, ai, "time", e.target.value)}
                        placeholder="09:00"
                        className="text-xs h-8"
                      />
                      <Input
                        value={act.name}
                        onChange={(e) => updateActivity(di, ai, "name", e.target.value)}
                        placeholder="Activity name"
                        className="text-xs h-8"
                      />
                      <Select value={act.type} onValueChange={(v) => updateActivity(di, ai, "type", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ACTIVITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="col-span-3">
                        <Input
                          value={act.description || ""}
                          onChange={(e) => updateActivity(di, ai, "description", e.target.value)}
                          placeholder="Description (optional)"
                          className="text-xs h-8"
                        />
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeActivity(di, ai)} className="text-destructive shrink-0 w-7 h-7" disabled={day.activities.length <= 1}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={() => addActivity(di)} className="w-full" data-testid={`add-activity-${di}`}>
                <Plus className="w-3 h-3 mr-1" /> Add Activity
              </Button>
            </div>

            {/* Meals */}
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 block">Meals Included</Label>
              <div className="flex gap-2">
                {["breakfast", "lunch", "dinner"].map((meal) => (
                  <button
                    key={meal}
                    onClick={() => toggleMeal(di, meal)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      day.meals.includes(meal) ? "bg-teal text-white border-teal" : "border-border text-muted-foreground hover:border-teal"
                    }`}
                    data-testid={`meal-${di}-${meal}`}
                  >
                    <Utensils className="w-3 h-3 inline mr-1" />{meal.charAt(0).toUpperCase() + meal.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Accommodation */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">Accommodation</Label>
                <Input
                  value={day.accommodation?.name || ""}
                  onChange={(e) => updateDay(di, "accommodation", { ...day.accommodation, name: e.target.value })}
                  placeholder="Hotel / Resort name"
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={day.accommodation?.type || "hotel"} onValueChange={(v) => updateDay(di, "accommodation", { ...day.accommodation, type: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["hotel", "resort", "tent", "homestay", "houseboat", "cottage", "none"].map((t) => (
                      <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Transport</Label>
                <Input
                  value={day.transport || ""}
                  onChange={(e) => updateDay(di, "transport", e.target.value)}
                  placeholder="AC Bus / Flight"
                  className="h-8 text-xs"
                />
              </div>
            </div>
          </Card>
        ))}

        <Button variant="outline" onClick={addDay} className="w-full py-6 border-dashed" data-testid="add-day-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Day {days.length + 1}
        </Button>

        <div className="flex items-center justify-between text-sm text-muted-foreground bg-muted/40 rounded-xl p-4">
          <span>{days.length} Day{days.length > 1 ? "s" : ""} / {Math.max(0, days.length - 1)} Night{days.length > 2 ? "s" : ""}</span>
          <Button onClick={saveItinerary} disabled={saving} data-testid="save-itinerary-bottom">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Itinerary
          </Button>
        </div>
      </div>
    </div>
  );
}
